// Hatha Yoga Trowbridge - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Users, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const HathaYogaTrowbridge = () => {
  usePageMeta({
    title: "Hatha Yoga Trowbridge | Yoga Classes | Annelie Lily",
    description: "Hatha yoga classes for Trowbridge residents. Traditional yoga for strength and flexibility, based in Frome, 15 minutes from Trowbridge. All levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-trowbridge/hatha-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Hatha Yoga Classes for Trowbridge",
    description: "Hatha yoga classes for Trowbridge residents at Annelie Lily Yoga in Frome. Traditional practice building strength, flexibility, and calm.",
    serviceType: "Yoga Classes",
    url: "https://annelielily.com/areas/yoga-trowbridge/hatha-yoga"
  });

  const faqs = [
    { question: "Where are Hatha yoga classes held?", answer: "All classes are at my studio in Frome, just 15 minutes from Trowbridge via the A361. Many Trowbridge residents make this journey weekly." },
    { question: "What is Hatha yoga?", answer: "Hatha is traditional yoga combining postures (asanas), breathing (pranayama), and relaxation. It builds strength and flexibility while calming the mind." },
    { question: "Is Hatha suitable for beginners?", answer: "Yes. I offer modifications for every pose, so you can work at your own level. Many students started as complete beginners." },
    { question: "What should I wear to class?", answer: "Comfortable clothing you can move freely in. Mats and props are provided, though you're welcome to bring your own mat." },
    { question: "How is this different from gym yoga?", answer: "Small groups (6-8 people) mean individual attention. Classes end with proper guided relaxation, not just a quick stretch. The pace is yours." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Hatha Yoga" currentSlug="areas/yoga-trowbridge/hatha-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Hatha Yoga for Trowbridge</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Hatha Yoga Trowbridge</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Traditional Hatha yoga for Trowbridge residents. Build strength, improve flexibility, 
              find calm — in small classes just 15 minutes from town.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />15 min from Trowbridge</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Hatha Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Snippet / Direct Answer */}
      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is Hatha yoga?</h2>
          <p className="text-neutral-700">
            Hatha yoga is the foundation of most modern yoga styles. It combines physical postures for strength and flexibility, breathing techniques for calm, and deep relaxation. Suitable for all levels, from complete beginners to experienced practitioners.
          </p>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who is Hatha yoga for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Complete beginners", desc: "Learn proper foundations with modifications for every pose" },
              { title: "Those returning to exercise", desc: "A gentle way to rebuild strength and flexibility" },
              { title: "Desk workers", desc: "Counter rounded shoulders and tight hips from sitting" },
              { title: "Anyone seeking balance", desc: "Equal focus on body, breath, and relaxation" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a Hatha class</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Centering</h3>
                <p className="text-sm">We begin by settling in, bringing awareness to the breath and leaving the day behind.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Standing poses</h3>
                <p className="text-sm">Building strength, balance, and grounding. I offer variations so everyone can participate.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Floor work</h3>
                <p className="text-sm">Seated stretches, twists for the spine, and gentle inversions. This is where deep release happens.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Guided relaxation</h3>
                <p className="text-sm">Every class ends with yoga nidra — deep relaxation that helps body and mind integrate the practice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Trowbridge Residents Choose Us */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Trowbridge residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              <Link to="/areas/yoga-trowbridge" className="text-gold hover:underline">Trowbridge</Link> to Frome is just 
              15 minutes — close enough for a weekly yoga practice that doesn't feel like a trek.
            </p>
            <p>
              My classes are small (6-8 people) so I can actually see how you're moving and offer guidance that's 
              relevant to your body. <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">New to yoga?</Link> That's 
              fine — I teach at a pace that welcomes all levels.
            </p>
            <p>
              For something slower, try <Link to="/areas/yoga-trowbridge/yin-yoga" className="text-gold hover:underline">Yin yoga for Trowbridge</Link>. 
              Or for individual attention, <Link to="/areas/yoga-trowbridge/private-yoga" className="text-gold hover:underline">private sessions</Link> are available.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of Hatha Yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, text: "Small group attention" },
              { icon: Clock, text: "Work at your pace" },
              { icon: Heart, text: "Reduces stress" },
              { icon: Check, text: "All levels welcome" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions About Hatha Yoga in Trowbridge</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">
            I'm Annelie, a qualified yoga teacher offering small, welcoming classes where everyone gets individual attention. 
            <Link to="/about" className="text-gold hover:underline ml-1">Learn more about my approach</Link>, or 
            <Link to="/about" className="text-gold hover:underline ml-1">read what students say</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try Hatha Yoga?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Trowbridge residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-trowbridge/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga Trowbridge</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-trowbridge/private-yoga" className="text-sm text-gold hover:underline">Private Sessions</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-trowbridge" className="text-sm text-gold hover:underline">All Classes Trowbridge</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga (Main Page)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HathaYogaTrowbridge;
